import type React from "react";
import { useEffect, useState } from "react";

import { faBook, faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { faPersonWalking } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Divider, InputAdornment, Pagination, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { UserCourseLesson } from "api/schemas/UserCourse";
import { useGetLessons } from "api/services/content/hooks";
import { useGetExercises } from "api/services/content/hooks/useGetExercises";
import type { Equipment, Movement, Position } from "api/services/content/schemas/shared";
import type { PatientTreatmentScheduleExercise } from "api/services/treatment/schemas/Treatment";
import { useProfileContext } from "contexts/ProfileContext";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import { MUISelect } from "shared/atoms/MUI";
import { Notification } from "shared/atoms/Notification";
import { sortAlphabetically } from "utils/array/sort";
import { useEquipmentOptions, useMovementOptions, usePositionOptions, useTargetJointOptions } from "utils/form";

import type { PreviewActivity } from "../Modals";
import { ActivityPreviewModal } from "../Modals";
import { ActivityLibrarySkeleton } from "../Skeleton";
import type { ActivityType, HandleAddActivity } from "../types";

import { ActivityLibraryItem } from "./ActivityLibraryItem";

const paginationSettings = {
  limit: 50,
};

export type ActiveTab = "exercises" | "lessons";

interface ActivityLibrarySidebarProps {
  activeTab: ActiveTab;
  addedExercises: PatientTreatmentScheduleExercise[];
  addedLessons: UserCourseLesson[];
  onAddActivity: HandleAddActivity;
  onRemoveActivity: (id: number, type: ActivityType) => void;
  setActiveTab: React.Dispatch<React.SetStateAction<ActiveTab>>;
}

export const ActivityLibrarySidebar: React.FC<ActivityLibrarySidebarProps> = ({
  activeTab,
  addedExercises,
  addedLessons,
  onAddActivity,
  onRemoveActivity,
  setActiveTab,
}) => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const equipmentOptions = useEquipmentOptions();
  const movementOptions = useMovementOptions();
  const positionOptions = usePositionOptions();
  const targetJointOptions = useTargetJointOptions();

  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [movement, setMovement] = useState<Movement[]>([]);
  const [position, setPosition] = useState<Position[]>([]);

  const [selectedJoint, setSelectedJoint] = useState<string>("all");
  const [previewActivity, setPreviewActivity] = useState<PreviewActivity | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: exercisesData,
    error: isExercisesError,
    isLoading: isExercisesLoading,
    isFetching: isExercisesFetching,
  } = useGetExercises(
    {
      joint: selectedJoint !== "all" ? selectedJoint : undefined,
      limit: paginationSettings.limit,
      locale: profile?.locale || "en-US",
      equipment: equipment.length > 0 ? equipment : undefined,
      movement: movement.length > 0 ? movement : undefined,
      position: position.length > 0 ? position : undefined,
      page: currentPage,
      search: debouncedSearchTerm || undefined,
    },
    {
      enabled: activeTab === "exercises",
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  const {
    data: lessonsData,
    error: isLessonsError,
    isLoading: isLessonsLoading,
    isFetching: isLessonsFetching,
  } = useGetLessons(
    {
      joint: selectedJoint !== "all" ? selectedJoint : undefined,
      limit: paginationSettings.limit,
      locale: profile?.locale || "en-US",
      page: currentPage,
      search: debouncedSearchTerm || undefined,
    },
    {
      enabled: activeTab === "lessons",
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const exercises = exercisesData?.items || [];
  const exercisesTotalCount = exercisesData?.metadata?.total_count || 0;
  const lessons = lessonsData?.items || [];
  const lessonsTotalCount = lessonsData?.metadata?.total_count || 0;

  const isLoading = isExercisesLoading || isLessonsLoading;
  const isFetching = isExercisesFetching || isLessonsFetching;
  const isError = isExercisesError || isLessonsError;

  let exercisesContent: React.ReactNode;
  if (isFetching) {
    exercisesContent = <ActivityLibrarySkeleton />;
  } else if (exercises.length === 0) {
    exercisesContent = <EmptyState>{t("common.exercises_icu", { count: 0 })}</EmptyState>;
  } else {
    exercisesContent = (
      <Stack spacing={1}>
        {exercises.map(exercise => (
          <ActivityLibraryItem
            key={exercise.exercise_id}
            activity={exercise}
            isAdded={addedExercises.some(e => e.exercise_id === exercise.exercise_id)}
            type="exercise"
            onAddActivity={onAddActivity}
            onRemoveActivity={onRemoveActivity}
            onPreview={id => setPreviewActivity({ id, part: 1, type: "exercise" })}
          />
        ))}
      </Stack>
    );
  }

  let lessonsContent: React.ReactNode;
  if (isFetching) {
    lessonsContent = <ActivityLibrarySkeleton />;
  } else if (lessons.length === 0) {
    lessonsContent = <EmptyState>{t("common.lessons_icu", { count: 0 })}</EmptyState>;
  } else {
    lessonsContent = (
      <Stack spacing={1}>
        {lessons.map(lesson => (
          <ActivityLibraryItem
            key={lesson.id}
            activity={lesson}
            isAdded={addedLessons.some(l => l.id === lesson.id)}
            type="lesson"
            onAddActivity={onAddActivity}
            onRemoveActivity={onRemoveActivity}
            onPreview={id => setPreviewActivity({ id, part: 1, type: "lesson" })}
          />
        ))}
      </Stack>
    );
  }

  const jointOptions = [
    { label: t("patients.pain_location.all"), value: "all" },
    ...sortAlphabetically({ list: targetJointOptions }),
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [equipment.length, movement.length, position.length, selectedJoint, debouncedSearchTerm, activeTab]);

  return (
    <Stack>
      <Box
        sx={{
          padding: "28px 12px 16px",
          bgcolor: "#fff",
        }}
      >
        <ButtonsGroup
          group={[
            {
              value: "exercises",
              label: t("common.exercises"),
              icon: <FontAwesomeIcon icon={faPersonWalking} />,
            },
            {
              value: "lessons",
              label: t("common.lessons"),
              icon: <FontAwesomeIcon icon={faBook} />,
            },
          ]}
          active={activeTab}
          setActive={value => {
            setActiveTab(value as ActiveTab);
            setSearchTerm("");
            setDebouncedSearchTerm("");
            setCurrentPage(1);
          }}
        />
      </Box>

      <Stack spacing={1.5} padding={1.5}>
        <TextField
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          placeholder={t("library.search_placeholder")}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </InputAdornment>
              ),
            },
          }}
        />

        <MUISelect
          options={jointOptions}
          value={selectedJoint}
          onChange={e => setSelectedJoint(e.target.value as string)}
        />

        <Box display="flex" gap="10px">
          <MUISelect
            label={t("library.exercises.fields.equipment")}
            options={equipmentOptions}
            value={equipment}
            onChange={event => setEquipment(event.target.value as Equipment[])}
            multiple
            fullWidth
          />
          <MUISelect
            label={t("library.exercises.fields.movement")}
            options={movementOptions}
            value={movement}
            onChange={event => setMovement(event.target.value as Movement[])}
            multiple
            fullWidth
          />
          <MUISelect
            label={t("library.exercises.fields.position")}
            options={positionOptions}
            value={position}
            onChange={event => setPosition(event.target.value as Position[])}
            multiple
            fullWidth
          />
        </Box>
      </Stack>

      <Divider />

      <Stack spacing={3} padding={1.5}>
        {isLoading && <ActivityLibrarySkeleton />}

        {!isLoading && activeTab === "exercises" && (
          <>
            {exercisesTotalCount > 0 && (
              <Box display="flex" justifyContent="center">
                <Pagination
                  count={Math.ceil(exercisesTotalCount / paginationSettings.limit)}
                  page={currentPage}
                  onChange={(_event, newPage) => setCurrentPage(newPage)}
                />
              </Box>
            )}

            {exercisesContent}

            {exercisesTotalCount > 0 && (
              <Box display="flex" justifyContent="center">
                <Pagination
                  count={Math.ceil(exercisesTotalCount / paginationSettings.limit)}
                  page={currentPage}
                  onChange={(_event, newPage) => setCurrentPage(newPage)}
                />
              </Box>
            )}
          </>
        )}

        {!isLoading && activeTab === "lessons" && (
          <>
            {lessonsTotalCount > 0 && (
              <Box display="flex" justifyContent="center">
                <Pagination
                  count={Math.ceil(lessonsTotalCount / paginationSettings.limit)}
                  page={currentPage}
                  onChange={(_event, newPage) => setCurrentPage(newPage)}
                />
              </Box>
            )}

            {lessonsContent}

            {lessonsTotalCount > 0 && (
              <Box display="flex" justifyContent="center">
                <Pagination
                  count={Math.ceil(lessonsTotalCount / paginationSettings.limit)}
                  page={currentPage}
                  onChange={(_event, newPage) => setCurrentPage(newPage)}
                />
              </Box>
            )}
          </>
        )}

        {isError && (
          <Notification variant="danger" style={{ marginTop: "16px" }}>
            {t("errors.could_not_load_data", {
              dataType: activeTab === "exercises" ? t("common.exercises") : t("common.lessons"),
            })}
          </Notification>
        )}
      </Stack>

      {previewActivity && (
        <ActivityPreviewModal
          activity={previewActivity}
          onClose={() => {
            setPreviewActivity(null);
          }}
        />
      )}
    </Stack>
  );
};

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  font-size: 14px;
  color: #8391a1;
`;
